import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';

import { MembershipV2Service } from 'src/app/services/membership-v2.service';
import { AuthService } from 'src/app/services/auth.service';
import { NotificationService } from 'src/app/shared/notification.service';
import { LedgerDialogComponent } from '../membership-v2/dialogs/ledger-dialog.component';
import { EmployeeDialogComponent } from '../membership-v2/dialogs/employee-dialog.component';
import { ResetPasswordDialogComponent } from '../membership-v2/dialogs/reset-password-dialog.component';

// Super-admin GLOBAL employee dashboard (v2 root model). Because employees /
// memberships / groups are root collections, this searches across EVERY site and
// company at once — impossible on the legacy per-site page. Loads once, filters
// client-side. Gated to super-admins (redirects otherwise).
@Component({
  selector: 'employees-global',
  templateUrl: './employees-global.component.html',
  styleUrls: ['./employees-global.component.css']
})
export class EmployeesGlobalComponent implements OnInit {

  loading = true;
  search = '';
  employees: any[] = [];
  filtered: any[] = [];
  selected: any = null;
  selectedMemberships: any[] = [];

  private byEmployee: { [employeeId: string]: any[] } = {};
  private allGroups: any[] = [];
  private groupsMap: { [id: string]: string } = {};
  private companiesMap: { [id: string]: string } = {};
  private sitesMap: { [id: string]: { name: string, companyId: string } } = {};

  constructor(
    private svc: MembershipV2Service,
    private auth: AuthService,
    private router: Router,
    private dialog: MatDialog,
    private notifier: NotificationService
  ) { }

  ngOnInit() {
    // super-admin only
    this.auth.getUser$().subscribe(u => {
      if (!this.auth.isSuperAdmin(u)) {
        this.notifier.showNotification('error', 'Super-admin access only');
        this.router.navigate(['/dashboard/sites']);
      }
    });
    this.load();
  }

  private async load() {
    this.loading = true;
    try {
      // critical data
      const [employees, memberships] = await Promise.all([
        this.svc.getAllEmployeesOnce(),
        this.svc.getAllMembershipsOnce()
      ]);
      // label maps are non-critical — degrade to raw IDs if any read is denied
      const [allGroups, companiesMap, sitesMap] = await Promise.all([
        this.svc.getAllGroupsOnce().catch(() => [] as any[]),
        this.svc.getCompaniesMapOnce().catch(() => ({})),
        this.svc.getSitesMapOnce().catch(() => ({}))
      ]);
      this.allGroups = allGroups;
      this.groupsMap = {};
      allGroups.forEach(g => { if (g.groupId) { this.groupsMap[g.groupId] = g.name || g.groupId; } });
      this.companiesMap = companiesMap;
      this.sitesMap = sitesMap;

      // index memberships by their employeeId
      this.byEmployee = {};
      memberships.forEach(m => {
        const eid = m.employeeId;
        if (!eid) { return; }
        (this.byEmployee[eid] = this.byEmployee[eid] || []).push(m);
      });

      this.employees = employees
        .map(e => ({ ...e, membershipCount: (this.byEmployee[e.id] || []).length }))
        .sort((a, b) => (a.name || a.email || '').localeCompare(b.name || b.email || ''));
      this.applyFilter();
    } catch (e) {
      this.notifier.showNotification('error', 'Could not load employees');
    } finally {
      this.loading = false;
    }
  }

  applyFilter() {
    const q = (this.search || '').trim().toLowerCase();
    const base = q
      ? this.employees.filter(e => `${e.name || ''} ${e.email || ''}`.toLowerCase().includes(q))
      : this.employees;
    // cap the rendered list for performance; search narrows it
    this.filtered = base.slice(0, 200);
  }

  select(emp: any) {
    this.selected = emp;
    this.selectedMemberships = (this.byEmployee[emp.id] || []).map(m => ({
      ...m,
      siteLabel: this.siteLabel(m.siteId),
      companyLabel: this.companyLabel(m.companyId, m.siteId),
      groupLabel: m.groupId ? (this.groupsMap[m.groupId] || m.groupId) : '—'
    }));
  }

  siteLabel(siteId: string) {
    const s = this.sitesMap[siteId];
    return s ? s.name : (siteId || '—');
  }
  companyLabel(companyId: string, siteId: string) {
    const cid = companyId || (this.sitesMap[siteId] ? this.sitesMap[siteId].companyId : '');
    return this.companiesMap[cid] || cid || '—';
  }

  // Super-admin sets a new password for the selected employee directly, e.g.
  // when they report a login issue and the admin resets it on their behalf.
  resetPassword() {
    if (!this.selected) { return; }
    const ref = this.dialog.open(ResetPasswordDialogComponent, {
      width: '420px',
      data: { name: this.selected.name || this.selected.email, email: this.selected.email }
    });
    ref.afterClosed().subscribe(async res => {
      if (!res || !res.password) { return; }
      const ok = await this.svc.adminResetPassword(this.selected.id, res.password);
      if (ok) {
        alert(`New password for ${this.selected.email}:\n\n${res.password}\n\nShare it with them now — it won't be shown again.`);
      }
    });
  }

  // Alternative to resetPassword(): the employee sets their own password via
  // an emailed link, instead of the admin handing them one directly.
  sendResetEmail() {
    if (!this.selected || !this.selected.email) { return; }
    this.svc.sendResetEmail(this.selected.email);
  }

  openLedger(m: any) {
    this.dialog.open(LedgerDialogComponent, {
      width: '640px',
      data: {
        name: (this.selected && (this.selected.name || this.selected.email)) || m.name,
        credit: m.credit,
        transactions$: this.svc.getMembershipTransactions$(m.dbid)
      }
    });
  }

  // Edit one membership. The group picker is scoped to that membership's site.
  editMembership(m: any) {
    const groups = this.allGroups.filter(g => g.companyId === m.companyId && g.siteId === m.siteId);
    const ref = this.dialog.open(EmployeeDialogComponent, {
      width: '460px',
      data: { mode: 'edit', employee: m, groups }
    });
    ref.afterClosed().subscribe(res => {
      if (res && res.action === 'edit') {
        this.svc.updateMembership(m.dbid, res.patch).then(() => {
          // reflect in the loaded (non-realtime) data, then re-render
          const orig = (this.byEmployee[this.selected.id] || []).find(x => x.dbid === m.dbid);
          if (orig) { Object.assign(orig, res.patch); }
          this.select(this.selected);
        });
      }
    });
  }

  // Soft-delete one membership (removes them from that site only).
  deleteMembership(m: any) {
    if (!confirm(`Remove ${this.selected?.email || 'this employee'}'s membership at "${m.siteLabel}"? Soft delete — the record is kept, not erased.`)) { return; }
    this.svc.softDeleteMembership(m.dbid).then(() => {
      this.byEmployee[this.selected.id] = (this.byEmployee[this.selected.id] || []).filter(x => x.dbid !== m.dbid);
      const e = this.employees.find(x => x.id === this.selected.id);
      if (e) { e.membershipCount = (this.byEmployee[this.selected.id] || []).length; }
      this.select(this.selected);
    });
  }
}
